import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { can } from '../../utils/roleUtils';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button, IconButton,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Alert, Tooltip,
  useMediaQuery, useTheme, Tabs, Tab, Stack, Switch, FormControl,
  InputLabel, Select, MenuItem, Badge, FormControlLabel, Radio, RadioGroup
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachFileIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import WalletService from '../../services/wallet.service';
import ApiService from '../../services/api.service';
import {
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';

const WALLET_TABS = ['customers', 'topups'];
const BANKING_TABS = ['bank', 'cheques', 'cards'];
const TAB_SX = { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 40 };

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const WalletsPage = () => {
  // Session user for permission checks (this page receives no currentUser prop).
  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
  })();
  const canWriteFin = can(sessionUser, 'finance', 'write');
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The tab lives in the URL (?tab=customers|topups) so links open the right tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = Math.max(0, WALLET_TABS.indexOf(searchParams.get('tab')));
  const setActiveTab = (index) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', WALLET_TABS[index]);
    setSearchParams(next, { replace: true });
  };
  const [summary, setSummary] = useState(null);

  // Customer Wallets
  const [wallets, setWallets] = useState([]);
  const [walletSearch, setWalletSearch] = useState('');
  const [walletPage, setWalletPage] = useState(0);
  const [walletRowsPerPage, setWalletRowsPerPage] = useState(25);

  // Removed clients that still hold a wallet balance to refund to the customer
  const [pendingRefund, setPendingRefund] = useState([]);
  const [refundDialog, setRefundDialog] = useState({ open: false, wallet: null, bankId: '', submitting: false, error: null });

  // Top-Up Requests
  const [topups, setTopups] = useState([]);
  const [topupSearch, setTopupSearch] = useState('');
  const [topupStatusFilter, setTopupStatusFilter] = useState('ALL');
  const [topupPage, setTopupPage] = useState(0);
  const [topupRowsPerPage, setTopupRowsPerPage] = useState(25);
  const [reviewDialog, setReviewDialog] = useState({ open: false, topup: null });
  const [reviewAction, setReviewAction] = useState('approve');
  const [reviewReason, setReviewReason] = useState('');
  const [reviewBankRef, setReviewBankRef] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // Bank accounts, for the refund dialog
  const [bankAccounts, setBankAccounts] = useState([]);

  // Pulls a readable message out of our ApiResponse error envelope.
  // Backend shape: { success: false, error: "...", data?: { field: msg, ... } }
  const extractErrorMessage = (err, fallback) => {
    const body = err?.response?.data;
    if (!body) return err?.message || fallback;
    // Field-level validation errors (MethodArgumentNotValidException handler)
    if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
      const fieldErrors = Object.entries(body.data)
        .map(([field, msg]) => `${field}: ${msg}`)
        .join('; ');
      if (fieldErrors) return fieldErrors;
    }
    return body.error || body.message || fallback;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, walletsRes, topupsRes, banksRes, pendingRefundRes] = await Promise.all([
        WalletService.getWalletSummary(),
        WalletService.getCustomerWallets({ search: walletSearch }),
        WalletService.getTopupRequests({
          search: topupSearch,
          status: topupStatusFilter !== 'ALL' ? topupStatusFilter : undefined
        }),
        WalletService.getBankAccounts(),
        WalletService.getWalletsPendingRefund()
      ]);
      setSummary(summaryRes);
      setWallets(walletsRes.data);
      setTopups(topupsRes.data);
      setBankAccounts(banksRes);
      setPendingRefund(pendingRefundRes || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load wallet data'));
    } finally {
      setLoading(false);
    }
  }, [walletSearch, topupSearch, topupStatusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Refund a removed client's leftover wallet balance to the customer, paid from a TC bank ──
  const openRefundDialog = (w) => setRefundDialog({ open: true, wallet: w, bankId: '', submitting: false, error: null });
  const closeRefundDialog = () => { if (refundDialog.submitting) return; setRefundDialog({ open: false, wallet: null, bankId: '', submitting: false, error: null }); };
  const submitRefund = async () => {
    const { wallet, bankId } = refundDialog;
    if (!wallet || !bankId) return;
    setRefundDialog((d) => ({ ...d, submitting: true, error: null }));
    try {
      await WalletService.refundCustomerWallet({ organizationId: wallet.internalOrganizationId, bankAccountId: bankId });
      setRefundDialog({ open: false, wallet: null, bankId: '', submitting: false, error: null });
      await fetchData();
    } catch (err) {
      setRefundDialog((d) => ({ ...d, submitting: false, error: extractErrorMessage(err, 'Failed to refund wallet balance') }));
    }
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
  const formatDateTime = (date) => date ? new Date(date).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  // ── Review Dialog Handlers ──
  const openReviewDialog = (topup) => {
    setReviewDialog({ open: true, topup });
    setReviewAction('approve');
    setReviewReason('');
    setReviewBankRef(topup?.bankReference || '');
  };

  const closeReviewDialog = () => {
    setReviewDialog({ open: false, topup: null });
    setReviewAction('approve');
    setReviewReason('');
    setReviewBankRef('');
  };

  const handleReviewSubmit = async () => {
    const { topup } = reviewDialog;
    if (!topup) return;
    setReviewSubmitting(true);
    try {
      if (reviewAction === 'approve') {
        await WalletService.approveTopup(topup.id, reviewBankRef.trim() || undefined);
      } else {
        await WalletService.rejectTopup(topup.id, reviewReason);
      }
      closeReviewDialog();
      fetchData();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to process review'));
      closeReviewDialog();
      fetchData();
    } finally {
      setReviewSubmitting(false);
    }
  };

  // ── Filtered/paginated data ──
  const filteredWallets = wallets;
  const paginatedWallets = filteredWallets.slice(walletPage * walletRowsPerPage, walletPage * walletRowsPerPage + walletRowsPerPage);
  const filteredTopups = topups;
  const paginatedTopups = filteredTopups.slice(topupPage * topupRowsPerPage, topupPage * topupRowsPerPage + topupRowsPerPage);

  const pendingCount = summary?.pendingTopups || 0;

  // ── Summary Cards ──
  const renderSummaryCards = () => (
    <ProTableStats
      loading={!summary}
      cards={[
        { label: 'Approved Balance', value: WalletService.formatCurrency(summary?.totalApprovedBalance || 0), color: 'success.main' },
        { label: 'Unapproved Balance', value: WalletService.formatCurrency(summary?.totalUnapprovedBalance || 0), color: 'warning.main' },
        { label: 'Customers', value: summary?.totalCustomers ?? 0, onClick: () => setActiveTab(0), highlight: activeTab === 0 },
        {
          label: 'Pending Top-ups', value: summary?.pendingTopups ?? 0,
          color: (summary?.pendingTopups || 0) > 0 ? 'error.main' : undefined,
          onClick: () => { setActiveTab(1); setTopupStatusFilter('PENDING'); setTopupPage(0); },
          highlight: activeTab === 1,
        },
      ]}
    />
  );

  const toolbarInputSx = { '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: fontSize.sm } };
  const refreshButton = (
    <Tooltip title="Refresh">
      <IconButton size="small" onClick={fetchData} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <RefreshIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  // ── Proof View / Download ──
  const handleOpenProof = async (topupId) => {
    try {
      const response = await ApiService.getTopUpRequestProofUrl(topupId);
      const url = response?.data?.data?.download_url || response?.data?.data?.downloadUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to open proof file'));
    }
  };

  const handleDownloadProof = async (topupId, filename) => {
    try {
      const response = await ApiService.getTopUpRequestProofUrl(topupId);
      const url = response?.data?.data?.download_url || response?.data?.data?.downloadUrl;
      if (!url) return;
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || '';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to download proof file'));
    }
  };

  // ── Tab 1: Customer Wallets ──
  const refundBal = Number(refundDialog.wallet?.approvedBalance || 0);
  const refundSelectedBank = bankAccounts.find((b) => b.id === refundDialog.bankId) || null;
  const refundInsufficient = !!refundSelectedBank && Number(refundSelectedBank.balance || 0) < refundBal;
  const refundConfirmDisabled = refundDialog.submitting || !refundDialog.bankId || refundInsufficient;

  const renderWalletsTab = () => (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" placeholder="Search by customer name or code..."
          value={walletSearch} onChange={(e) => { setWalletSearch(e.target.value); setWalletPage(0); }}
          sx={{ flex: '1 1 200px', minWidth: 200, ...toolbarInputSx }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {filteredWallets.length} wallets
          </Typography>
          {refreshButton}
        </Box>
      </Box>

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box> :
          paginatedWallets.length === 0 ? <Paper sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No customer wallets found</Typography></Paper> :
          paginatedWallets.map((w) => (
            <Card key={w.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/wallets/${w.id}`)}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Box>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main' }}>{w.organizationName}</Typography>
                    <Typography sx={{ fontSize: fontSize.xs }}>{w.organizationCode}</Typography>
                  </Box>
                  <ChevronRightIcon color="action" />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs }}>Approved</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: w.approvedBalance > 0 ? '#4caf50' : '#f44336' }}>{WalletService.formatCurrency(w.approvedBalance)}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography sx={{ fontSize: fontSize.xs }}>Unapproved</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: '#ff9800' }}>{WalletService.formatCurrency(w.unapprovedBalance)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Customer Name</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Code</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Approved Balance</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Unapproved Balance</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Total Balance</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Last Activity</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : paginatedWallets.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No customer wallets found</Typography></TableCell></TableRow>
                ) : paginatedWallets.map((w) => (
                  <TableRow key={w.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/wallets/${w.id}`)}>
                    <TableCell sx={{ fontSize: '13px', py: 0.75 }}>
                      <Typography sx={{ fontSize: '13px' }}>{w.organizationName}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', py: 0.75 }}>
                      {w.organizationCode || <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: w.approvedBalance > 0 ? 'success.main' : w.approvedBalance < 0 ? 'error.main' : 'text.secondary' }}>
                        {WalletService.formatCurrency(w.approvedBalance)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Typography sx={{ fontSize: '13px', color: w.unapprovedBalance > 0 ? 'warning.main' : 'text.secondary' }}>
                        {WalletService.formatCurrency(w.unapprovedBalance)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                        {WalletService.formatCurrency(w.approvedBalance + w.unapprovedBalance)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', py: 0.75 }}>
                      {formatDate(w.lastTransactionAt)}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View Wallet">
                        <IconButton size="small" onClick={() => navigate(`/wallets/${w.id}`)}>
                          <ViewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filteredWallets.length} page={walletPage}
            onPageChange={(e, p) => setWalletPage(p)} rowsPerPage={walletRowsPerPage}
            onRowsPerPageChange={(e) => { setWalletRowsPerPage(parseInt(e.target.value, 10)); setWalletPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}

      {pendingRefund.length > 0 && (
        <Paper sx={{ mt: 2, borderLeft: '4px solid #f44336' }}>
          <Box sx={{ p: 2, pb: 0.5 }}>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: '#f44336' }}>
              Removed clients — wallet balance to refund to customer ({pendingRefund.length})
            </Typography>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
              These clients were removed while their wallet still held money. Refund each balance to the customer, paid from a bank account.
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Customer Name</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Wallet Balance</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingRefund.map((w) => (
                  <TableRow key={w.id} sx={{ bgcolor: isDark ? 'rgba(244,67,54,0.08)' : '#fff5f5' }}>
                    <TableCell sx={{ fontSize: '13px', py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{w.organizationName}</Typography>
                        <Chip label="Refund pending" size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#f44336' }}>
                        {WalletService.formatCurrency(w.approvedBalance)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <Button size="small" variant="outlined" color="error" onClick={() => openRefundDialog(w)}>Refund to customer</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Refund a removed client's wallet balance to the customer, paid from a TC bank */}
      <Dialog open={refundDialog.open} onClose={closeRefundDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Refund wallet balance to customer</DialogTitle>
        <DialogContent>
          {refundDialog.wallet && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Refund <b>{refundDialog.wallet.organizationName}</b>&apos;s wallet balance of{' '}
                <b>{WalletService.formatCurrency(refundBal)}</b> and set the wallet to zero.
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>Refund from bank account</InputLabel>
                <Select label="Refund from bank account" value={refundDialog.bankId}
                  onChange={(e) => setRefundDialog((d) => ({ ...d, bankId: e.target.value }))}>
                  {bankAccounts.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {(b.bankName || b.accountName || 'Bank')} - {WalletService.formatCurrency(b.balance)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {WalletService.formatCurrency(refundBal)} will be debited from the selected bank account.
              </Typography>
              {refundInsufficient && <Alert severity="error" sx={{ mt: 1 }}>Selected bank account balance is insufficient</Alert>}
              {refundDialog.error && <Alert severity="error" sx={{ mt: 1 }}>{refundDialog.error}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeRefundDialog} disabled={refundDialog.submitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitRefund} disabled={refundConfirmDisabled}>
            {refundDialog.submitting ? 'Refunding...' : 'Refund to customer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // ── Tab 2: Top-Up Requests ──
  const renderTopupsTab = () => (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" placeholder="Search by customer..."
          value={topupSearch} onChange={(e) => { setTopupSearch(e.target.value); setTopupPage(0); }}
          sx={{ flex: '1 1 200px', minWidth: 200, ...toolbarInputSx }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 150, ...toolbarInputSx }}>
          <InputLabel sx={{ fontSize: fontSize.sm }}>Status</InputLabel>
          <Select value={topupStatusFilter} label="Status" onChange={(e) => { setTopupStatusFilter(e.target.value); setTopupPage(0); }}>
            <MenuItem value="ALL" sx={{ fontSize: fontSize.sm }}>All Statuses</MenuItem>
            <MenuItem value="PENDING" sx={{ fontSize: fontSize.sm }}>Pending</MenuItem>
            <MenuItem value="APPROVED" sx={{ fontSize: fontSize.sm }}>Approved</MenuItem>
            <MenuItem value="REJECTED" sx={{ fontSize: fontSize.sm }}>Rejected</MenuItem>
            <MenuItem value="CANCELLED" sx={{ fontSize: fontSize.sm }}>Cancelled</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          {refreshButton}
        </Box>
      </Box>

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box> :
          paginatedTopups.length === 0 ? <Paper sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No top-up requests found</Typography></Paper> :
          paginatedTopups.map((t) => (
            <Card key={t.id} variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Box>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{t.organizationName}</Typography>
                    <Typography sx={{ fontSize: fontSize.xs }}>{t.bankName} - {formatDate(t.createdAt)}</Typography>
                    {t.bankReference && (
                      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Ref {t.bankReference}</Typography>
                    )}
                  </Box>
                  <Chip label={WalletService.getTopupStatusLabel(t.status)} size="small" color={WalletService.getTopupStatusColor(t.status)} sx={{ fontSize: fontSize.xs, height: 22 }} />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{WalletService.formatCurrency(t.requestedAmount)}</Typography>
                  {t.status === 'PENDING' && canWriteFin && (
                    <Button size="small" variant="outlined" onClick={() => openReviewDialog(t)} sx={{ fontSize: fontSize.xs }}>Review</Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Submitted By</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Bank</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Requested</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Approved</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Date</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : paginatedTopups.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No top-up requests found</Typography></TableCell></TableRow>
                ) : paginatedTopups.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ py: 0.75 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{t.organizationName}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', py: 0.75 }}>
                      {/* TC-entered (wallet page) vs customer-submitted (portal) */}
                      {t.submittedBy === 'TYPING_CENTER' ? (
                        <>
                          <Typography sx={{ fontSize: '13px' }}>Typing center</Typography>
                          {t.submittedByName && (
                            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{t.submittedByName}</Typography>
                          )}
                        </>
                      ) : 'Customer'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', py: 0.75 }}>
                      {t.bankName}
                      {t.bankReference && (
                        <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Ref {t.bankReference}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{WalletService.formatCurrency(t.requestedAmount)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: t.approvedAmount ? '#4caf50' : 'text.secondary' }}>
                        {t.approvedAmount ? WalletService.formatCurrency(t.approvedAmount) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <Chip label={WalletService.getTopupStatusLabel(t.status)} size="small" color={WalletService.getTopupStatusColor(t.status)} sx={{ fontSize: fontSize.xs, height: 22 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', py: 0.75 }}>
                      {formatDate(t.createdAt)}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      {t.status === 'PENDING' && canWriteFin ? (
                        <Button size="small" variant="outlined" onClick={() => openReviewDialog(t)}
                          sx={{ fontSize: fontSize.xs, minWidth: 60, borderColor: 'primary.main', color: 'secondary.main' }}>
                          Review
                        </Button>
                      ) : (
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => openReviewDialog(t)}>
                            <ViewIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filteredTopups.length} page={topupPage}
            onPageChange={(e, p) => setTopupPage(p)} rowsPerPage={topupRowsPerPage}
            onRowsPerPageChange={(e) => { setTopupRowsPerPage(parseInt(e.target.value, 10)); setTopupPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}
    </>
  );

  // ── Main Render ──
  return (
    <Box sx={{ pb: isMobile ? 2 : 0, width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
        sx={{ mb: 2, minHeight: 40, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Customer Wallets" sx={TAB_SX} />
        <Tab sx={TAB_SX} label={
          pendingCount > 0
            ? <Badge badgeContent={pendingCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}>
                <Box component="span" sx={{ pr: 1.5 }}>Top-Up Requests</Box>
              </Badge>
            : 'Top-Up Requests'
        } />
      </Tabs>

      {renderSummaryCards()}

      {/* Tab Content */}
      {activeTab === 0 && renderWalletsTab()}
      {activeTab === 1 && renderTopupsTab()}
      {/* ── Review Top-Up Dialog ── */}
      <Dialog open={reviewDialog.open} onClose={closeReviewDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fontSize.md, fontWeight: 600, pb: 1 }}>
          Review Top-Up Request
        </DialogTitle>
        <DialogContent>
          {reviewDialog.topup && (
            <Box>
              {/* Request Info */}
              <Box sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5', borderRadius: 1, p: 2, mb: 2 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: fontSize.xs }}>Customer</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>{reviewDialog.topup.organizationName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: fontSize.xs }}>Bank</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>{reviewDialog.topup.bankName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: fontSize.xs }}>Requested Amount</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{WalletService.formatCurrency(reviewDialog.topup.requestedAmount)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: fontSize.xs }}>Date</Typography>
                    <Typography sx={{ fontSize: fontSize.sm }}>{formatDateTime(reviewDialog.topup.createdAt)}</Typography>
                  </Grid>
                  {reviewDialog.topup.bankReference && reviewDialog.topup.status !== 'PENDING' && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: fontSize.xs }}>Bank reference</Typography>
                      <Typography sx={{ fontSize: fontSize.sm }}>{reviewDialog.topup.bankReference}</Typography>
                    </Grid>
                  )}
                  {reviewDialog.topup.remarks && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: fontSize.xs }}>Remarks</Typography>
                      <Typography sx={{ fontSize: fontSize.sm }}>{reviewDialog.topup.remarks}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Alert severity="warning" sx={{ mb: 2, fontSize: fontSize.sm }}>
                Approve only when the money is in the account. If the proof is a cheque, reject this request and
                record it as a received cheque — it is added to the wallet when it clears.
              </Alert>

              {/* Proof File */}
              {reviewDialog.topup.proofUrl && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 0.5 }}>Proof File</Typography>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography sx={{ fontSize: fontSize.sm, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AttachFileIcon sx={{ fontSize: 16 }} />
                      {reviewDialog.topup.proofFilename || 'proof'}
                    </Typography>
                    <Button size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleOpenProof(reviewDialog.topup.id)}
                      sx={{ fontSize: fontSize.xs, textTransform: 'none' }}>
                      View
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleDownloadProof(reviewDialog.topup.id, reviewDialog.topup.proofFilename)}
                      sx={{ fontSize: fontSize.xs, textTransform: 'none' }}>
                      Download
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Already reviewed info */}
              {reviewDialog.topup.status !== 'PENDING' && (
                <Box sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5', borderRadius: 1, p: 2, mb: 2 }}>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 1 }}>Review Result</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: fontSize.xs }}>Status</Typography>
                      <Chip label={WalletService.getTopupStatusLabel(reviewDialog.topup.status)} size="small" color={WalletService.getTopupStatusColor(reviewDialog.topup.status)} sx={{ fontSize: fontSize.xs, mt: 0.5 }} />
                    </Grid>
                    {reviewDialog.topup.approvedAmount !== null && (
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: fontSize.xs }}>Approved Amount</Typography>
                        <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: '#4caf50' }}>{WalletService.formatCurrency(reviewDialog.topup.approvedAmount)}</Typography>
                      </Grid>
                    )}
                    {reviewDialog.topup.rejectionReason && (
                      <Grid item xs={12}>
                        <Typography sx={{ fontSize: fontSize.xs }}>Reason</Typography>
                        <Typography sx={{ fontSize: fontSize.sm }}>{reviewDialog.topup.rejectionReason}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: fontSize.xs }}>Reviewed At</Typography>
                      <Typography sx={{ fontSize: fontSize.sm }}>{formatDateTime(reviewDialog.topup.reviewedAt)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: fontSize.xs }}>Reviewed By</Typography>
                      <Typography sx={{ fontSize: fontSize.sm }}>{reviewDialog.topup.reviewedBy}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Action controls (only for PENDING) */}
              {reviewDialog.topup.status === 'PENDING' && (
                <Box>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 1 }}>Action</Typography>
                  <RadioGroup value={reviewAction} onChange={(e) => setReviewAction(e.target.value)} row>
                    <FormControlLabel value="approve" control={<Radio size="small" />}
                      label={<Typography sx={{ fontSize: fontSize.sm }}>Approve</Typography>} />
                    <FormControlLabel value="reject" control={<Radio size="small" />}
                      label={<Typography sx={{ fontSize: fontSize.sm, color: '#f44336' }}>Reject</Typography>} />
                  </RadioGroup>

                  {reviewAction === 'approve' && (
                    <TextField fullWidth size="small" label="Bank reference (optional)" sx={{ mt: 1 }}
                      value={reviewBankRef} onChange={(e) => setReviewBankRef(e.target.value)}
                      inputProps={{ maxLength: 100 }}
                      helperText="Pre-filled from the request; the value here replaces it and shows on the customer's statement" />
                  )}

                  {reviewAction === 'reject' && (
                    <TextField fullWidth size="small" label="Reason" multiline rows={2}
                      value={reviewReason}
                      onChange={(e) => setReviewReason(e.target.value)}
                      sx={{ mt: 1 }}
                      required
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeReviewDialog} size="small" sx={{ fontSize: fontSize.sm }}>
            {reviewDialog.topup?.status === 'PENDING' ? 'Cancel' : 'Close'}
          </Button>
          {reviewDialog.topup?.status === 'PENDING' && (
            <Button variant="contained" size="small"
              onClick={handleReviewSubmit}
              disabled={reviewSubmitting || (reviewAction === 'reject' && !reviewReason.trim())}
              sx={{
                fontSize: fontSize.sm,
                bgcolor: reviewAction === 'reject' ? '#f44336' : ('primary.main'),
                '&:hover': { bgcolor: reviewAction === 'reject' ? '#d32f2f' : ('primary.dark') }
              }}>
              {reviewSubmitting ? <CircularProgress size={18} color="inherit" /> :
                reviewAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Box>
  );
};

// Old links to the banking tabs (/wallets?tab=bank|cheques|cards) move to the Banking page.
const Wallets = () => {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  return BANKING_TABS.includes(tab) ? <Navigate to={`/banking?tab=${tab}`} replace /> : <WalletsPage />;
};

export default Wallets;
